'use client';

import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Color } from '@diritto/common/style/color';
import { ProfileSettingType } from '@diritto/common/account/constants/profile-setting.constant';
import { CategoryTypeById, CategoryTypeString } from '@diritto/common/content/constants';
import { ButtonType } from 'constants/button';
import { isPC } from 'constants/layout';
import { ModalParams, ModalType } from 'constants/modal';
import { Button } from 'ui-components/common/Button';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { useMedia } from 'ui-hooks/useMediaQuery';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';
import { useAuthState } from 'contexts/AuthProvider';
import { useAddProfileSettingMutation } from 'queries/profile/mutations/useAddProfileSettingMutation';
import { showToast } from 'utils/modal';
import { useHomeSettingState } from 'contexts/HomeSettingProvider';

const CONFIRM_FAVORITE_CATEGORY_STYLES = {
  titleContainer: 'flex flex-col justify-start items-start w-full gap-2 mb-4',
  title: 'justify-start items-start w-full',
  titleText: 'text-black text-sm font-bold',
  titleDescription: 'text-gray-600 text-xxs font-medium',
  categoryContainer: 'flex flex-col justify-start items-start w-full',
  categoryGuideMessageContainer: 'flex flex-col justify-start items-start',
  categoryGuideMessageImportant: 'flex flex-row items-start w-full gap-1 text-violet-500',
  categoryGuideMessageNormal: 'flex flex-row items-start w-full gap-1 text-gray-500',
  buttonContainer: 'flex flex-col justify-center items-center w-full mt-8 gap-1',
};

export const ConfirmFavoriteCategoryModal = () => {
  const { isPc } = useMedia();
  const { profile } = useAuthState();
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const { changeCategoryIds: changeFavoriteCategoryIds } = useHomeSettingState();

  const [state, setState] = useState<ModalParams[ModalType.ConfirmFavoriteCategory]>();

  const { mutate: addProfileSettingMutate } = useAddProfileSettingMutation();

  const handleHide = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    handleHide();
  }, [handleHide, state]);

  const handleSubmit = useCallback(
    async (categoryIds: number[]) => {
      if (!profile) {
        changeFavoriteCategoryIds(categoryIds);
        showToast('선호 장르가 적용되었어요.');
      } else {
        addProfileSettingMutate(
          {
            type: ProfileSettingType.FavoriteCategory,
            payload: {
              categoryIds,
            },
          },
          {
            onSuccess: () => {
              showToast('선호 장르가 적용되었어요.');
            },
            onError: () => {
              showToast('일시적인 오류로 선호 장르를 적용하지 못했어요. 잠시 뒤 다시 시도해주세요.');
            },
          },
        );
      }

      handleHide();
    },
    [handleHide, profile, addProfileSettingMutate, changeFavoriteCategoryIds],
  );

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.ConfirmFavoriteCategory]) => {
      setState(params);

      if (show) {
        showModal();
      } else {
        handleHide();
      }
    };

    ModalHandler.addEventListener(ModalType.ConfirmFavoriteCategory, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.ConfirmFavoriteCategory);
    };
  }, [handleHide, showModal]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal} isBottom={!isPc}>
      <StyledModal>
        <div className={CONFIRM_FAVORITE_CATEGORY_STYLES.titleContainer}>
          <div className={CONFIRM_FAVORITE_CATEGORY_STYLES.title}>
            <p className={CONFIRM_FAVORITE_CATEGORY_STYLES.titleText}>{`'${state?.title}'를 적용할까요?`}</p>
          </div>
          <p className={CONFIRM_FAVORITE_CATEGORY_STYLES.titleDescription}>적용하면 아래 장르들이 우선 노출돼요.</p>
        </div>

        <div className={CONFIRM_FAVORITE_CATEGORY_STYLES.categoryContainer}>
          <div className="inline-flex justify-center items-center h-[61px] w-full bg-gray-200 rounded mb-2">
            <p className="text-gray-700 text-xxs font-medium">
              {state?.categoryIds.map(it => CategoryTypeString[CategoryTypeById[it]]).join(', ')}
            </p>
          </div>
          <div className={CONFIRM_FAVORITE_CATEGORY_STYLES.categoryGuideMessageContainer}>
            <div className={CONFIRM_FAVORITE_CATEGORY_STYLES.categoryGuideMessageImportant}>
              <div className="items-center justify-start text-xxxs font-medium">·</div>
              <p className="flex text-xxxs font-medium">선호 장르는 퀵메뉴에서 언제든 변경할 수 있어요.</p>
            </div>
            <div className={CONFIRM_FAVORITE_CATEGORY_STYLES.categoryGuideMessageNormal}>
              <div className="items-center justify-start text-xxxs font-medium">·</div>
              <p className="flex text-xxxs font-medium">일부 콘텐츠 및 배너에는 적용되지 않을 수 있어요.</p>
            </div>
          </div>
        </div>

        <div className={CONFIRM_FAVORITE_CATEGORY_STYLES.buttonContainer}>
          <StyledButton label="적용하기" onClick={() => handleSubmit(state?.categoryIds ?? [])} />
          <StyledButton label="닫기" buttonType={ButtonType.SecondaryNoBorder} onClick={handleClose} />
        </div>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: 100vw;
  border-radius: 16px 16px 0 0;
  padding: 32px 24px 0 24px;

  @media (${isPC}) {
    width: 400px;
    border-radius: 16px;
    padding: 32px 24px 16px 24px;
  }
  background-color: ${Color.White};

  display: flex;
  flex-direction: column;
  align-items: center;
  ${SafeAreaBottomStyles(16)}
`;

const StyledButton = styled(Button)`
  width: 100%;
`;
